import React, { useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import KpiChartPanel from '../kpi-dashboard/charts/KpiChartPanel';
import type { BandwidthDayOfWeekPoint } from './bandwidthDashboardTypes';
import {
  bandwidthVerticalBarDim,
  bandwidthVerticalBarPalette,
  bandwidthVerticalBarSelected,
} from './bandwidthChartTheme';
import { formatAxisCompactK, formatDataLabelCompact } from '../kpi-dashboard/chartFormatters';

export interface BandwidthActivityByWeekdayChartProps {
  title: string;
  subtitle?: string;
  points: BandwidthDayOfWeekPoint[];
  /** 0 = Monday … 6 = Sunday when a weekday bar is selected (Dates drill). */
  selectedWeekdayIndex?: number | null;
  onWeekdayBarClick?: (monFirstIndex: number) => void;
  emptyHint?: string;
}

const BandwidthActivityByWeekdayChart: React.FC<BandwidthActivityByWeekdayChartProps> = ({
  title,
  subtitle,
  points,
  selectedWeekdayIndex = null,
  onWeekdayBarClick,
  emptyHint,
}) => {
  const { labels, counts, barColors } = useMemo(() => {
    const labelsInner = points.map((p) => p.dayLabel);
    const countsInner = points.map((p) => p.activityCount);
    const colorsInner = points.map((_, i) => {
      if (selectedWeekdayIndex === null || selectedWeekdayIndex === undefined) {
        return bandwidthVerticalBarPalette(i);
      }
      return i === selectedWeekdayIndex ? bandwidthVerticalBarSelected : bandwidthVerticalBarDim;
    });
    return { labels: labelsInner, counts: countsInner, barColors: colorsInner };
  }, [points, selectedWeekdayIndex]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: { top: 20, right: 4, left: 4, bottom: 4 } },
      onClick: (_event: unknown, elements: { index: number }[]) => {
        if (!onWeekdayBarClick || elements.length === 0) return;
        const idx = elements[0]?.index;
        if (typeof idx !== 'number' || idx < 0 || idx > 6) return;
        onWeekdayBarClick(idx);
      },
      onHover: (_event: unknown, elements: unknown[], chart: { canvas?: HTMLCanvasElement }) => {
        const canvas = chart?.canvas;
        if (!canvas) return;
        canvas.style.cursor = Array.isArray(elements) && elements.length > 0 ? 'pointer' : 'default';
      },
      interaction: { mode: 'index' as const, intersect: false },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx: { parsed: { y: number | null } }) => {
              const v = ctx.parsed.y;
              return typeof v === 'number' ? `${v.toLocaleString()} activities` : '';
            },
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: { color: '#64748b', font: { size: 10 } },
        },
        y: {
          beginAtZero: true,
          grid: { color: 'rgba(99, 102, 241, 0.14)' },
          ticks: {
            color: '#64748b',
            font: { size: 10 },
            callback: (value: string | number) => formatAxisCompactK(Number(value)),
          },
          title: { display: true, text: 'Activity count', color: '#64748b', font: { size: 11 } },
        },
      },
    }),
    [onWeekdayBarClick]
  );

  const hasAny = counts.some((c) => c > 0);

  if (points.length === 0 || !hasAny) {
    return (
      <KpiChartPanel
        title={title}
        titleClassName="!pl-0"
        className="h-full min-h-0 max-h-[min(28rem,70vh)] lg:max-h-[440px]"
        flexFill
        bodyClassName="px-3 pb-4 pt-4"
      >
        {subtitle ? <p className="mb-2 text-xs text-slate-500">{subtitle}</p> : null}
        <p className="py-12 text-center text-sm text-slate-500">
          {emptyHint ?? 'No data for the selected filters.'}
        </p>
      </KpiChartPanel>
    );
  }

  return (
    <KpiChartPanel
      title={title}
      titleClassName="!pl-0"
      className="h-full min-h-0 max-h-[min(28rem,70vh)] lg:max-h-[440px]"
      flexFill
      bodyClassName="px-3 pb-3 pt-3"
    >
      {subtitle ? <p className="mb-1 text-xs leading-snug text-slate-500">{subtitle}</p> : null}
      <div className="relative h-[260px] w-full min-h-0 flex-1 sm:h-[280px]">
        <Bar
          data={{
            labels,
            datasets: [
              {
                label: 'Activity count',
                data: counts,
                backgroundColor: barColors,
                borderWidth: 0,
                borderRadius: 4,
                maxBarThickness: 36,
                datalabels: {
                  display: true,
                  anchor: 'end',
                  align: 'end',
                  offset: 2,
                  clip: false,
                  color: '#334155',
                  font: { size: 10, weight: 600 },
                  formatter: (value: number) => formatDataLabelCompact(value),
                },
              },
            ],
          }}
          options={chartOptions}
        />
      </div>
    </KpiChartPanel>
  );
};

export default BandwidthActivityByWeekdayChart;
